/**
 * Upsell Binding Management for Shopify Cart
 * Handles the relationship between main products and their bound upsell items
 */

class UpsellBindingManager {
  constructor() {
    this.init();
  }

  init() {
    // Listen for cart updates to manage bound items
    document.addEventListener('cartUpdated', this.handleCartUpdate.bind(this));
    
    // Initialize quantity synchronization
    this.initQuantitySync();
  }

  /**
   * Handle cart updates to ensure bound items stay in sync
   */
  handleCartUpdate(event) {
    console.log('Cart updated, checking bound items...');
    this.syncBoundItemQuantities();
  }

  /**
   * Initialize quantity synchronization for bound items
   */
  initQuantitySync() {
    // Listen for quantity changes on main products
    document.addEventListener('change', (event) => {
      const quantityInput = event.target;
      
      if (quantityInput.classList.contains('quantity__input') && 
          quantityInput.name === 'updates[]') {
        
        this.handleMainProductQuantityChange(quantityInput);
      }
    });
  }

  /**
   * Handle quantity changes on main products and sync bound items
   */
  async handleMainProductQuantityChange(quantityInput) {
    const newQuantity = parseInt(quantityInput.value) || 0;
    const variantId = quantityInput.getAttribute('data-quantity-variant-id');
    
    if (!variantId) return;

    try {
      const cart = await this.getCart();
      const boundItems = this.findBoundItems(cart, variantId);
      
      if (boundItems.length > 0) {
        await this.updateBoundItemQuantities(boundItems, newQuantity);
      }
    } catch (error) {
      console.warn('Failed to sync bound item quantities:', error);
    }
  }

  /**
   * Sync bound item quantities with their main products
   */
  async syncBoundItemQuantities() {
    try {
      const cart = await this.getCart();
      const updates = {};
      let hasUpdates = false;

      cart.items.forEach(item => {
        // Skip non-bound items
        if (!item.properties || !item.properties._upsell_of) return;

        const mainVariantId = item.properties._upsell_of;
        const mainItem = cart.items.find(i => String(i.variant_id) === String(mainVariantId));

        if (mainItem) {
          // Sync quantity with main item
          if (item.quantity !== mainItem.quantity) {
            updates[item.key] = mainItem.quantity;
            hasUpdates = true;
          }
        } else {
          // Main item was removed, remove this bound item
          updates[item.key] = 0;
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        await this.updateCartItems(updates);
      }
    } catch (error) {
      console.warn('Failed to sync bound items:', error);
    }
  }

  /**
   * Find all items bound to a specific main product variant
   */
  findBoundItems(cart, mainVariantId) {
    return cart.items.filter(item => {
      const boundToVariant = item.properties && item.properties._upsell_of;
      const isHiddenUpsell = item.properties && item.properties._is_hidden_upsell === "true";
      return boundToVariant === String(mainVariantId) && isHiddenUpsell;
    });
  }

  /**
   * Update quantities for bound items
   */
  async updateBoundItemQuantities(boundItems, newQuantity) {
    const updates = {};
    
    boundItems.forEach(item => {
      updates[item.key] = newQuantity;
    });

    if (Object.keys(updates).length > 0) {
      await this.updateCartItems(updates);
    }
  }

  /**
   * Update cart items via Shopify's cart API
   */
  async updateCartItems(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update cart items:', error);
      throw error;
    }
  }

  /**
   * Get current cart data
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get cart:', error);
      throw error;
    }
  }

  /**
   * Calculate total price including bound items for display purposes
   */
  calculateTotalWithBoundItems(cart) {
    let total = 0;
    
    cart.items.forEach(item => {
      // Count all items in total (visible and hidden)
      total += item.final_line_price;
    });
    
    return total;
  }

  /**
   * Get visible items count (excluding hidden upsells)
   */
  getVisibleItemsCount(cart) {
    return cart.items.filter(item => {
      const isHiddenUpsell = item.properties && item.properties._is_hidden_upsell === "true";
      return !isHiddenUpsell;
    }).length;
  }

  /**
   * Remove all bound items for a specific main product
   */
  async removeBoundItems(mainVariantId) {
    try {
      const cart = await this.getCart();
      const boundItems = this.findBoundItems(cart, mainVariantId);
      
      if (boundItems.length > 0) {
        const updates = {};
        boundItems.forEach(item => {
          updates[item.key] = 0;
        });
        
        await this.updateCartItems(updates);
      }
    } catch (error) {
      console.warn('Failed to remove bound items:', error);
    }
  }
}

// Initialize the binding manager when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.upsellBindingManager = new UpsellBindingManager();
});

// Also initialize if script loads after DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.upsellBindingManager) {
      window.upsellBindingManager = new UpsellBindingManager();
    }
  });
} else {
  if (!window.upsellBindingManager) {
    window.upsellBindingManager = new UpsellBindingManager();
  }
}
